import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, CopyIcon, CheckIcon, XIcon, PlayIcon, HistoryIcon, GitBranchIcon, SaveIcon, EyeIcon, CodeIcon, RefreshCwIcon, TagIcon, ToggleLeftIcon, ToggleRightIcon, DollarSignIcon, LinkIcon, LayersIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { RuleSetEditor } from './rules/RuleSetEditor';
import { TestRunner } from './rules/TestRunner';
export function RulesManager({
  locale
}) {
  const [activeView, setActiveView] = useState('overview');
  const [selectedRuleSet, setSelectedRuleSet] = useState(null);
  const [showTestRunner, setShowTestRunner] = useState(false);
  // Sample data for rule sets
  const [ruleSets, setRuleSets] = useState([{
    id: 'rule-001',
    name: 'Invoice Approval Workflow',
    description: 'Determines the approval path for invoices based on amount, vendor, and GL code',
    module: 'AP',
    triggerEvent: 'Invoice Submitted',
    status: 'Active',
    lastModified: '2023-10-15T14:30:00Z',
    version: '1.2',
    conditions: [{
      id: 'cond-001',
      type: 'group',
      operator: 'AND',
      children: [{
        id: 'cond-002',
        type: 'condition',
        field: 'Value',
        operator: 'greaterThan',
        value: '10000'
      }, {
        id: 'cond-003',
        type: 'condition',
        field: 'GL Code',
        operator: 'equals',
        value: '5000.5200'
      }]
    }],
    actions: [{
      id: 'action-001',
      type: 'route',
      target: 'Finance Manager',
      targetType: 'role'
    }, {
      id: 'action-002',
      type: 'notify',
      target: 'john.smith@example.com',
      targetType: 'email',
      template: 'High Value Invoice'
    }]
  }, {
    id: 'rule-002',
    name: 'PO Auto-Approval',
    description: 'Automatically approves purchase orders under certain conditions',
    module: 'Procurement',
    triggerEvent: 'PO Created',
    status: 'Draft',
    lastModified: '2023-10-10T09:15:00Z',
    version: '0.5',
    conditions: [{
      id: 'cond-004',
      type: 'group',
      operator: 'OR',
      children: [{
        id: 'cond-005',
        type: 'condition',
        field: 'Value',
        operator: 'lessThan',
        value: '1000'
      }, {
        id: 'cond-006',
        type: 'condition',
        field: 'Vendor',
        operator: 'in',
        value: ['vendor-001', 'vendor-002']
      }]
    }],
    actions: [{
      id: 'action-003',
      type: 'auto-approve'
    }]
  }, {
    id: 'rule-003',
    name: 'Vendor Compliance Check',
    description: 'Triggers compliance checks for vendors based on country and transaction value',
    module: 'Vendor',
    triggerEvent: 'Vendor Created',
    status: 'Inactive',
    lastModified: '2023-09-28T11:45:00Z',
    version: '1.0',
    conditions: [{
      id: 'cond-007',
      type: 'group',
      operator: 'AND',
      children: [{
        id: 'cond-008',
        type: 'condition',
        field: 'Country',
        operator: 'in',
        value: ['RU', 'CN', 'IR']
      }, {
        id: 'cond-009',
        type: 'group',
        operator: 'OR',
        children: [{
          id: 'cond-010',
          type: 'condition',
          field: 'Vendor Status',
          operator: 'equals',
          value: 'New'
        }, {
          id: 'cond-011',
          type: 'condition',
          field: 'Value',
          operator: 'greaterThan',
          value: '50000'
        }]
      }]
    }],
    actions: [{
      id: 'action-004',
      type: 'trigger-workflow',
      workflowId: 'compliance-check'
    }, {
      id: 'action-005',
      type: 'notify',
      target: 'compliance-team',
      targetType: 'group',
      template: 'Compliance Review Required'
    }]
  },
  // New Financial Delegation With Partial Categories rule
  {
    id: 'rule-004',
    name: 'Financial Delegation With Partial Categories',
    description: 'Checks document totals or line subtotals against financial delegation thresholds for users. Automatically assigns approvers based on category mappings.',
    module: 'Financial',
    triggerEvent: 'Document Submitted',
    status: 'Active',
    lastModified: '2023-11-01T10:15:00Z',
    version: '1.0',
    ruleType: 'Financial Delegation',
    scope: 'Entity',
    documentTypes: ['Purchase Orders', 'Invoices', 'Credit Notes'],
    execution: 'Automatic',
    parameters: [{
      id: 'param-001',
      name: 'FailureTag',
      type: 'Text',
      value: 'FD-FAIL'
    }, {
      id: 'param-002',
      name: 'CheckFdoaAgainstLineSubtotal',
      type: 'Boolean',
      value: true
    }, {
      id: 'param-003',
      name: 'UseFdoaCategories',
      type: 'Boolean',
      value: true
    }, {
      id: 'param-004',
      name: 'MultipleApprovers',
      type: 'Boolean',
      value: true
    }, {
      id: 'param-005',
      name: 'DisableCurrencyExchange',
      type: 'Boolean',
      value: false
    }, {
      id: 'param-006',
      name: 'DataSource',
      type: 'Dropdown',
      value: 'GL Coding'
    }, {
      id: 'param-007',
      name: 'DataSourceRecord',
      type: 'Text',
      value: 'AccountCode'
    }, {
      id: 'param-008',
      name: 'DataSourceField',
      type: 'Text',
      value: 'Code'
    }, {
      id: 'param-009',
      name: 'LinkedRules',
      type: 'Multi-Select',
      value: ['VendorComplianceCheck', 'HighValueFlagging']
    }],
    conditions: [{
      id: 'cond-fd-001',
      type: 'group',
      operator: 'AND',
      children: [{
        id: 'cond-fd-002',
        type: 'condition',
        field: 'Document.Total',
        operator: 'greaterThan',
        value: 'User.Delegation.UpperLimit',
        isDynamic: true
      }, {
        id: 'cond-fd-003',
        type: 'condition',
        field: 'UseFdoaCategories',
        operator: 'equals',
        value: 'TRUE'
      }, {
        id: 'cond-fd-004',
        type: 'condition',
        field: 'Document.GLCode',
        operator: 'between',
        value: ['1000', '2000']
      }]
    }],
    actions: [{
      id: 'action-fd-001',
      type: 'delegation-route',
      delegationReference: true,
      description: 'Route for Delegation'
    }, {
      id: 'action-fd-002',
      type: 'auto-approve',
      condition: 'ELSE',
      description: 'Auto-Approve if conditions not met'
    }, {
      id: 'action-fd-003',
      type: 'tag',
      tagName: 'FD-FAIL',
      condition: 'ON_FAILURE',
      description: 'Tag document if no delegation found'
    }],
    linkedRules: [{
      id: 'linked-001',
      ruleName: 'VendorComplianceCheck',
      executeWhen: 'AFTER'
    }, {
      id: 'linked-002',
      ruleName: 'EscalationRule',
      executeWhen: 'ON_FAILURE'
    }],
    jsonOutput: `{
  "WorkflowName": "FinancialApproval",
  "Rules": [
    {
      "RuleName": "Financial Delegation With Partial Categories",
      "Expression": "input.Total > GetDelegationThreshold(input.GLCode, input.Entity)",
      "Actions": {
        "OnSuccess": {
          "AssignDelegation": true
        },
        "OnFailure": {
          "TagDocument": "FD-FAIL"
        }
      },
      "LinkedRules": ["VendorComplianceCheck", "EscalationRule"]
    }
  ]
}`
  }]);
  // Sample data for available modules and triggers
  const modules = [{
    id: 'AP',
    name: 'Accounts Payable'
  }, {
    id: 'Procurement',
    name: 'Procurement'
  }, {
    id: 'Vendor',
    name: 'Vendor Management'
  }, {
    id: 'GL',
    name: 'General Ledger'
  }, {
    id: 'Expense',
    name: 'Expense Management'
  }, {
    id: 'Financial',
    name: 'Financial Management'
  }];
  const triggerEvents = {
    AP: [{
      id: 'Invoice Submitted',
      name: 'Invoice Submitted'
    }, {
      id: 'Invoice Approved',
      name: 'Invoice Approved'
    }, {
      id: 'Payment Initiated',
      name: 'Payment Initiated'
    }],
    Procurement: [{
      id: 'PO Created',
      name: 'PO Created'
    }, {
      id: 'PO Approved',
      name: 'PO Approved'
    }, {
      id: 'Requisition Submitted',
      name: 'Requisition Submitted'
    }],
    Vendor: [{
      id: 'Vendor Created',
      name: 'Vendor Created'
    }, {
      id: 'Vendor Updated',
      name: 'Vendor Updated'
    }, {
      id: 'Vendor Approved',
      name: 'Vendor Approved'
    }],
    GL: [{
      id: 'Journal Entry Created',
      name: 'Journal Entry Created'
    }, {
      id: 'Period Closing',
      name: 'Period Closing'
    }],
    Expense: [{
      id: 'Expense Report Submitted',
      name: 'Expense Report Submitted'
    }, {
      id: 'Expense Approved',
      name: 'Expense Approved'
    }],
    Financial: [{
      id: 'Document Submitted',
      name: 'Document Submitted'
    }, {
      id: 'Document Approved',
      name: 'Document Approved'
    }, {
      id: 'Document Rejected',
      name: 'Document Rejected'
    }]
  };
  // Sample data for reusable conditions
  const [reusableConditions, setReusableConditions] = useState([{
    id: 'template-001',
    name: 'High Value Transaction',
    description: 'Identifies transactions above $10,000',
    condition: {
      id: 'cond-temp-001',
      type: 'condition',
      field: 'Value',
      operator: 'greaterThan',
      value: '10000'
    }
  }, {
    id: 'template-002',
    name: 'High Risk Country',
    description: 'Identifies transactions from high risk countries',
    condition: {
      id: 'cond-temp-002',
      type: 'condition',
      field: 'Country',
      operator: 'in',
      value: ['RU', 'CN', 'IR', 'KP', 'SY']
    }
  }, {
    id: 'template-003',
    name: 'Financial Delegation Reference',
    description: 'References user delegation thresholds for financial approval',
    condition: {
      id: 'cond-temp-003',
      type: 'condition',
      field: 'Document.Total',
      operator: 'greaterThan',
      value: 'User.Delegation.UpperLimit',
      isDynamic: true
    }
  }]);
  // Handler for creating a new rule set
  const handleCreateRuleSet = () => {
    const newRuleSet = {
      id: `rule-${new Date().getTime()}`,
      name: 'New Rule Set',
      description: '',
      module: '',
      triggerEvent: '',
      status: 'Draft',
      lastModified: new Date().toISOString(),
      version: '0.1',
      conditions: [],
      actions: []
    };
    setSelectedRuleSet(newRuleSet);
    setActiveView('edit');
  };
  // Handler for editing a rule set
  const handleEditRuleSet = ruleSet => {
    setSelectedRuleSet(ruleSet);
    setActiveView('edit');
  };
  // Handler for duplicating a rule set
  const handleDuplicateRuleSet = ruleSet => {
    const duplicatedRuleSet = {
      ...ruleSet,
      id: `rule-${new Date().getTime()}`,
      name: `Copy of ${ruleSet.name}`,
      status: 'Draft',
      lastModified: new Date().toISOString(),
      version: '0.1'
    };
    setRuleSets([...ruleSets, duplicatedRuleSet]);
  };
  // Handler for activating/deactivating a rule set
  const handleToggleRuleSetStatus = ruleSetId => {
    setRuleSets(ruleSets.map(rs => rs.id === ruleSetId ? {
      ...rs,
      status: rs.status === 'Active' ? 'Inactive' : rs.status === 'Inactive' ? 'Active' : rs.status,
      lastModified: new Date().toISOString()
    } : rs));
  };
  // Handler for deleting a rule set
  const handleDeleteRuleSet = ruleSetId => {
    setRuleSets(ruleSets.filter(rs => rs.id !== ruleSetId));
  };
  // Handler for testing a rule set
  const handleTestRuleSet = ruleSet => {
    setSelectedRuleSet(ruleSet);
    setShowTestRunner(true);
    setActiveView('edit');
  };
  // Handler for saving a rule set
  const handleSaveRuleSet = updatedRuleSet => {
    if (updatedRuleSet.id) {
      // Update existing rule set
      setRuleSets(ruleSets.map(rs => rs.id === updatedRuleSet.id ? {
        ...updatedRuleSet,
        lastModified: new Date().toISOString()
      } : rs));
    } else {
      // Create new rule set
      const newRuleSet = {
        ...updatedRuleSet,
        id: `rule-${new Date().getTime()}`,
        lastModified: new Date().toISOString()
      };
      setRuleSets([...ruleSets, newRuleSet]);
    }
    setActiveView('overview');
    setSelectedRuleSet(null);
  };
  // Format date for display
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Get rule type icon
  const getRuleTypeIcon = ruleSet => {
    if (ruleSet.ruleType === 'Financial Delegation') {
      return <DollarSignIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-9432" />;
    } else if (ruleSet.module === 'Vendor') {
      return <TagIcon className="h-4 w-4 text-orange-500 mr-2" data-id="element-9433" />;
    } else {
      return <GitBranchIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-9434" />;
    }
  };
  // Render the Rule Sets overview
  const renderRuleSetsOverview = () => {
    return <div className="space-y-4" data-id="element-9435">
        <div className="flex justify-between items-center mb-4" data-id="element-9436">
          <div data-id="element-9437">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-9438">Rule Sets</h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9439">
              Manage rule sets for business process automation
            </p>
          </div>
          <button onClick={handleCreateRuleSet} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9440">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-9441" />
            Create Rule Set
          </button>
        </div>
        <div className="flex flex-col" data-id="element-9442">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-9443">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-9444">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-9445">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-9446">
                  <thead className="bg-gray-50" data-id="element-9447">
                    <tr data-id="element-9448">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9449">
                        Rule Set
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9450">
                        Module / Trigger
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9451">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9452">
                        Last Modified
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9453">
                        Version
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9454">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-9455">
                    {ruleSets.map(ruleSet => <tr key={ruleSet.id} className={ruleSet.ruleType === 'Financial Delegation' ? 'bg-indigo-50' : ''} data-id="element-9456">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-9457">
                          <div className="flex items-center" data-id="element-9458">
                            {getRuleTypeIcon(ruleSet)}
                            <div data-id="element-9459">
                              <div className="text-sm font-medium text-gray-900" data-id="element-9460">
                                {ruleSet.name}
                                {ruleSet.ruleType === 'Financial Delegation' && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-9461">
                                    FDOA
                                  </span>}
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-9462">
                                {ruleSet.description}
                              </div>
                              {ruleSet.linkedRules && ruleSet.linkedRules.length > 0 && <div className="flex items-center mt-1 text-xs text-indigo-600" data-id="element-9463">
                                    <LinkIcon className="h-3 w-3 mr-1" data-id="element-9464" />
                                    Linked Rules: {ruleSet.linkedRules.length}
                                  </div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-9465">
                          <div className="text-sm text-gray-900" data-id="element-9466">
                            {ruleSet.module}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-9467">
                            {ruleSet.triggerEvent}
                          </div>
                          {ruleSet.documentTypes && <div className="text-xs text-gray-500 mt-1" data-id="element-9468">
                              {ruleSet.documentTypes.join(', ')}
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-9469">
                          {ruleSet.status === 'Active' && <StatusBadge status="success" text="Active" data-id="element-9470" />}
                          {ruleSet.status === 'Draft' && <StatusBadge status="info" text="Draft" data-id="element-9471" />}
                          {ruleSet.status === 'Inactive' && <StatusBadge status="error" text="Inactive" data-id="element-9472" />}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-9473">
                          <div className="text-sm text-gray-900" data-id="element-9474">
                            {formatDate(ruleSet.lastModified)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-9475">
                          <div className="text-sm text-gray-900" data-id="element-9476">
                            v{ruleSet.version}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9477">
                          <div className="flex justify-end space-x-2" data-id="element-9478">
                            <button onClick={() => handleEditRuleSet(ruleSet)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-9479">
                              <EditIcon className="h-4 w-4" data-id="element-9480" />
                            </button>
                            <button onClick={() => handleTestRuleSet(ruleSet)} className="text-blue-600 hover:text-blue-900" title="Test" data-id="element-9481">
                              <PlayIcon className="h-4 w-4" data-id="element-9482" />
                            </button>
                            <button onClick={() => handleDuplicateRuleSet(ruleSet)} className="text-green-600 hover:text-green-900" title="Duplicate" data-id="element-9483">
                              <CopyIcon className="h-4 w-4" data-id="element-9484" />
                            </button>
                            <button onClick={() => handleToggleRuleSetStatus(ruleSet.id)} className={`${ruleSet.status === 'Active' ? 'text-amber-600 hover:text-amber-900' : 'text-green-600 hover:text-green-900'}`} title={ruleSet.status === 'Active' ? 'Deactivate' : 'Activate'} data-id="element-9485">
                              {ruleSet.status === 'Active' ? <ToggleRightIcon className="h-4 w-4" data-id="element-9486" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-9487" />}
                            </button>
                            <button onClick={() => handleDeleteRuleSet(ruleSet.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-9488">
                              <TrashIcon className="h-4 w-4" data-id="element-9489" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
                {ruleSets.length === 0 && <div className="text-center py-4 text-sm text-gray-500" data-id="element-9490">
                    No rule sets found. Click "Create Rule Set" to create one.
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  // Render the Financial Delegation Rule details
  const renderFinancialDelegationRuleDetails = () => {
    if (!selectedRuleSet || selectedRuleSet.ruleType !== 'Financial Delegation') {
      return null;
    }
    return <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6" data-id="element-9491">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9492">
          <div className="flex justify-between items-center" data-id="element-9493">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9494">
              Financial Delegation Configuration
            </h3>
            <div className="flex items-center text-sm text-indigo-600" data-id="element-9495">
              <LayersIcon className="h-4 w-4 mr-1" data-id="element-9496" />
              References Delegations Module
            </div>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-9497">
            This rule uses the Delegations configuration to determine approval
            routing
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200" data-id="element-9498">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-9499">Parameters</h4>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-9500">
            {selectedRuleSet.parameters.map(param => <div key={param.id} className="col-span-1" data-id="element-9501">
                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-9502">
                  {param.name}
                </label>
                <div className="text-sm text-gray-900" data-id="element-9503">
                  {param.type === 'Boolean' ? param.value ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-9504">
                        True
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-9505">
                        False
                      </span> : param.type === 'Multi-Select' ? <div className="flex flex-wrap gap-1" data-id="element-9506">
                      {Array.isArray(param.value) ? param.value.map((val, idx) => <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-9507">
                              {val}
                            </span>) : param.value}
                    </div> : param.value}
                </div>
              </div>)}
          </div>
        </div>
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200" data-id="element-9508">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-9509">
            Condition Expression
          </h4>
          <div className="bg-gray-50 p-4 rounded text-sm font-mono text-gray-800 whitespace-pre-wrap overflow-auto" data-id="element-9510">
            {"IF (Document.Total > User.Delegation.UpperLimit) AND UseFdoaCategories = TRUE AND Document.GLCode BETWEEN '1000' AND '2000' THEN Route for Delegation ELSE Auto-Approve"}
          </div>
        </div>
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200" data-id="element-9511">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-9512">Actions</h4>
          <div className="space-y-4" data-id="element-9513">
            {selectedRuleSet.actions.map(action => <div key={action.id} className="flex items-start p-3 bg-gray-50 rounded-md" data-id="element-9514">
                <div className="mr-3 mt-0.5" data-id="element-9515">
                  {action.type === 'delegation-route' ? <DollarSignIcon className="h-5 w-5 text-green-500" data-id="element-9516" /> : action.type === 'auto-approve' ? <CheckIcon className="h-5 w-5 text-blue-500" data-id="element-9517" /> : action.type === 'tag' ? <TagIcon className="h-5 w-5 text-amber-500" data-id="element-9518" /> : <GitBranchIcon className="h-5 w-5 text-gray-500" data-id="element-9519" />}
                </div>
                <div data-id="element-9520">
                  <div className="text-sm font-medium text-gray-900" data-id="element-9521">
                    {action.description}
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-9522">
                    {action.condition && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mt-1" data-id="element-9523">
                        {action.condition}
                      </span>}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-9524">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-9525">
            Linked Rules
          </h4>
          {selectedRuleSet.linkedRules && selectedRuleSet.linkedRules.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-9526">
              {selectedRuleSet.linkedRules.map(linkedRule => <li key={linkedRule.id} className="py-3 flex justify-between" data-id="element-9527">
                  <div className="flex items-center" data-id="element-9528">
                    <LinkIcon className="h-4 w-4 text-indigo-500 mr-2" data-id="element-9529" />
                    <span className="text-sm text-gray-900" data-id="element-9530">
                      {linkedRule.ruleName}
                    </span>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-9531">
                    {linkedRule.executeWhen}
                  </span>
                </li>)}
            </ul> : <p className="text-sm text-gray-500" data-id="element-9532">No linked rules configured</p>}
        </div>
      </div>;
  };
  // Render JSON output for rule
  const renderJsonOutput = () => {
    if (!selectedRuleSet || !selectedRuleSet.jsonOutput) {
      return null;
    }
    return <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6" data-id="element-9533">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9534">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9535">
            RulesEngine JSON Output
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-9536">
            Generated JSON definition for Microsoft RulesEngine
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-9537">
          <pre className="bg-gray-50 p-4 rounded text-sm font-mono text-gray-800 whitespace-pre-wrap overflow-auto" data-id="element-9538">
            {selectedRuleSet.jsonOutput}
          </pre>
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-9539">
      <div className="mb-8" data-id="element-9540">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-9541">Rules</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-9542">
          Define conditional logic for business process automation
        </p>
      </div>
      {/* Main Content */}
      {activeView === 'overview' ? renderRuleSetsOverview() : <div className="space-y-4" data-id="element-9543">
          <div className="flex justify-between items-center mb-4" data-id="element-9544">
            <button onClick={() => {
          setActiveView('overview');
          setSelectedRuleSet(null);
          setShowTestRunner(false);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9545">
              ← Back to Rule Sets
            </button>
            <div className="flex space-x-2" data-id="element-9546">
              {!showTestRunner && selectedRuleSet?.ruleType !== 'Financial Delegation' && <button onClick={() => setShowTestRunner(true)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9547">
                    <PlayIcon className="h-4 w-4 mr-2" data-id="element-9548" />
                    Test Rule
                  </button>}
              {showTestRunner && <button onClick={() => setShowTestRunner(false)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9549">
                  <EditIcon className="h-4 w-4 mr-2" data-id="element-9550" />
                  Edit Rule
                </button>}
            </div>
          </div>
          <div className={`grid grid-cols-1 ${showTestRunner ? 'lg:grid-cols-2' : ''} gap-6`} data-id="element-9551">
            {!showTestRunner && selectedRuleSet?.ruleType !== 'Financial Delegation' && <div className="col-span-1" data-id="element-9552">
                  <RuleSetEditor ruleSet={selectedRuleSet} modules={modules} triggerEvents={triggerEvents} reusableConditions={reusableConditions} onSave={handleSaveRuleSet} locale={locale} data-id="element-9553" />
                </div>}
            {selectedRuleSet?.ruleType === 'Financial Delegation' && <>
                <div className="col-span-1" data-id="element-9554">
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9555">
                    <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9556">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9557">
                        Rule Set Details
                      </h3>
                    </div>
                    <div className="px-4 py-5 sm:p-6" data-id="element-9558">
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2" data-id="element-9559">
                        <div className="sm:col-span-1" data-id="element-9560">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9561">
                            Name
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9562">
                            {selectedRuleSet?.name}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-9563">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9564">
                            Version
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9565">
                            v{selectedRuleSet?.version}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-9566">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9567">
                            Module
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9568">
                            {selectedRuleSet?.module}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-9569">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9570">
                            Trigger
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9571">
                            {selectedRuleSet?.triggerEvent}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-9572">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9573">
                            Rule Type
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9574">
                            {selectedRuleSet?.ruleType}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-9575">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9576">
                            Scope
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9577">
                            {selectedRuleSet?.scope}
                          </dd>
                        </div>
                        <div className="sm:col-span-2" data-id="element-9578">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9579">
                            Document Types
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9580">
                            {selectedRuleSet?.documentTypes?.join(', ')}
                          </dd>
                        </div>
                        <div className="sm:col-span-2" data-id="element-9581">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9582">
                            Description
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9583">
                            {selectedRuleSet?.description}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                  {renderFinancialDelegationRuleDetails()}
                  {renderJsonOutput()}
                </div>
              </>}
            {showTestRunner && <>
                <div className="col-span-1" data-id="element-9584">
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9585">
                    <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9586">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9587">
                        Rule Set Details
                      </h3>
                    </div>
                    <div className="px-4 py-5 sm:p-6" data-id="element-9588">
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2" data-id="element-9589">
                        <div className="sm:col-span-1" data-id="element-9590">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9591">
                            Name
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9592">
                            {selectedRuleSet?.name}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-9593">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9594">
                            Version
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9595">
                            v{selectedRuleSet?.version}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-9596">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9597">
                            Module
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9598">
                            {selectedRuleSet?.module}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-9599">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9600">
                            Trigger
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9601">
                            {selectedRuleSet?.triggerEvent}
                          </dd>
                        </div>
                        <div className="sm:col-span-2" data-id="element-9602">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-9603">
                            Description
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-9604">
                            {selectedRuleSet?.description}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="col-span-1" data-id="element-9605">
                  <TestRunner ruleSet={selectedRuleSet} locale={locale} data-id="element-9606" />
                </div>
              </>}
          </div>
        </div>}
    </div>;
}